import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/apiRouting';
import './MicroSurvey.css';

type SurveyMode = 'store' | 'request';

interface SurveyData {
  requestId: string;
  storeName: string;
  alreadyCompleted: boolean;
}

const MicroSurvey: React.FC<{ mode: SurveyMode }> = ({ mode }) => {
  const { storeId, requestId: paramRequestId } = useParams<{ storeId: string; requestId: string }>();
  const [survey, setSurvey] = useState<SurveyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ routedTo: string; googleReviewUrl: string | null; message: string } | null>(null);

  useEffect(() => {
    loadSurvey();
  }, []);

  const loadSurvey = async () => {
    try {
      const url = mode === 'store'
        ? API_ENDPOINTS.surveyWalkIn(storeId!)
        : API_ENDPOINTS.surveyContext(paramRequestId!);

      const res = await fetch(url);
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Survey not found');
        return;
      }

      setSurvey(data.survey);
    } catch (e) {
      setError('Unable to load survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitResponse = async () => {
    if (!survey || !rating) return;
    setSubmitting(true);

    try {
      const res = await fetch(API_ENDPOINTS.surveyRespond(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId: survey.requestId,
          rating,
          comment: comment.trim() || undefined
        })
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to submit');
        return;
      }

      setResult(data);

      // Auto-redirect to Google after a short delay if routed there
      if (data.routedTo === 'google' && data.googleReviewUrl) {
        setTimeout(() => {
          window.location.href = data.googleReviewUrl;
        }, 2500);
      }
    } catch (e) {
      setError('Unable to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="micro-survey-page">
        <div className="micro-survey-card">
          <div className="micro-survey-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="micro-survey-page">
        <div className="micro-survey-card">
          <div className="micro-survey-error">{error}</div>
        </div>
      </div>
    );
  }

  if (survey?.alreadyCompleted) {
    return (
      <div className="micro-survey-page">
        <div className="micro-survey-card">
          <h1>Thank You</h1>
          <p>You have already submitted feedback. We appreciate it!</p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="micro-survey-page">
        <div className="micro-survey-card">
          <h1>Thank You!</h1>
          <p>{result.message}</p>
          {result.routedTo === 'google' && result.googleReviewUrl && (
            <div className="micro-survey-redirect">
              <p>Redirecting you to Google Reviews...</p>
              <a href={result.googleReviewUrl} className="micro-survey-google-btn">
                Leave a Google Review
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="micro-survey-page">
      <div className="micro-survey-card">
        <h1>How was your visit?</h1>
        <p className="micro-survey-store">{survey?.storeName}</p>

        <div className="micro-survey-stars">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              className={`micro-survey-star ${star <= (hoveredRating || rating) ? 'active' : ''}`}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              aria-label={`${star} star${star > 1 ? 's' : ''}`}
            >
              &#9733;
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="micro-survey-rating-label">
            {rating <= 2 ? 'We can do better' : rating === 3 ? 'It was okay' : rating === 4 ? 'Great experience' : 'Outstanding!'}
          </p>
        )}

        <textarea
          className="micro-survey-comment"
          placeholder="Tell us more (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={500}
        />

        <button
          className="micro-survey-submit"
          onClick={submitResponse}
          disabled={!rating || submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
};

export default MicroSurvey;
